/**
 * Image caching utility to prevent repeated image URL calls
 */

// In-memory cache for image URLs
const imageCache = new Map();

// Cache expiration time (24 hours)
const CACHE_EXPIRATION = 24 * 60 * 60 * 1000;

/**
 * Get cached image URL or generate and cache new one
 * @param {string} key - Cache key (original image path)
 * @param {function} generator - Function to generate URL if not cached
 * @returns {string} Cached or newly generated URL
 */
export const getCachedImageUrl = (key, generator) => {
  if (!key) return null;

  const cached = imageCache.get(key);
  if (cached) {
    if (Date.now() - cached.timestamp < CACHE_EXPIRATION) {
      return cached.url;
    }
    imageCache.delete(key); // Clear expired cache
  }

  const url = generator();
  imageCache.set(key, {
    url,
    timestamp: Date.now()
  });
  return url;
};

/**
 * Clear the entire image cache
 */
export const clearImageCache = () => {
  imageCache.clear();
};

/**
 * Remove a specific image from cache
 * @param {string} key - Cache key to remove
 */
export const removeFromCache = (key) => {
  imageCache.delete(key);
};

/**
 * Get cache statistics
 * @returns {object} Cache stats
 */
export const getCacheStats = () => {
  const now = Date.now();
  let validEntries = 0;
  let expiredEntries = 0;

  imageCache.forEach(entry => {
    if (now - entry.timestamp < CACHE_EXPIRATION) {
      validEntries++;
    } else {
      expiredEntries++;
    }
  });

  return {
    totalEntries: imageCache.size,
    validEntries,
    expiredEntries
  };
};
